/* eslint-disable no-nested-ternary */
import React from 'react'
import { Box, Flex, Text } from 'theme-ui'
import { AssetDetailsSortBy } from '@spectral/types'
import { concat, isEmpty, omit, pick, sortBy as LsortBy } from 'lodash'
import { Loading } from '../../../../components/loading'
import IacFilesList from '../iac-files-list/view'
import IacListSideFilters, {
  SideFilterType,
} from '../../../../components/filters/side-filters'
import {
  buildAssignableMembersData,
  filtersSetup,
} from '../../shared/list-page-setup'
import useDebouncedFieldFilter from '../../../../hooks/useDebouncedFieldFilter'
import DynamicDataFilter from '../../../../components/filters/dynamic-data-filter'
import useFiltersActions from '../../../../hooks/useFiltersActions'
import AppliedFilters from '../../../../components/applied-filters'
import {
  removeEmptyFilters,
  transformAssigneeFiltersToDisplay,
  toFilterView,
  transformFiltersToDisplay,
} from '../../shared/transform-filters'
import PathFilter from '../../filters/path-filter'
import FiltersSidePanel from '../../../../containers/filters-side-panel'

export const sortOptions = [
  { key: AssetDetailsSortBy.PATH, title: 'Path' },
  { key: AssetDetailsSortBy.COUNT, title: 'Total issues' },
]

const IacView = ({
  asset,
  iacFiles,
  filtersData,
  currentQueryStringParams,
  assignableMembers,
  isLoadingAssignableMembers,
  isAssignableMembersLoaded,
  setQueryStringParam,
  clearQueryStringParam,
  trackSort,
  trackFilter,
  expandedFile,
  onPaginationChange,
  pagination,
  onIssueSelectionChanged,
  isRefreshing,
  isRefreshingIacFileDetails,
  actionHandlers,
  selectedIssues,
  exportActions,
  isDownloadingExport,
  integrations,
  onActionIntegrationSubmit,
  loaded,
  onChangeFileExpansion,
  isDataFiltered,
}) => {
  const { issueId } = currentQueryStringParams
  const [
    onDebouncedValueChange,
    currentDebouncedValues,
  ] = useDebouncedFieldFilter(
    {
      path: currentQueryStringParams.path,
      assignees: currentQueryStringParams.assignees,
      detectorId: currentQueryStringParams.detectorId,
      resources: currentQueryStringParams.resources,
      severity: currentQueryStringParams.severity,
      tag: currentQueryStringParams.tag,
      status: currentQueryStringParams.status,
    },
    setQueryStringParam,
    clearQueryStringParam,
    trackFilter,
    currentQueryStringParams
  )

  const isHiddenFilters =
    isEmpty(filtersData.status) && !isRefreshing && !isDataFiltered

  const filtersMetadata = filtersSetup(
    {
      assignableMembers: {
        ...buildAssignableMembersData(filtersData.assignees),
        isActive: !isHiddenFilters || filtersData.assignees?.length > 0,
      },
      detectors: filtersData?.detectorId || [],
      severity: filtersData?.severity || [],
      status: filtersData?.status || [],
      tags: filtersData?.tags || [],
    },
    [
      {
        filter: {
          key: 'resources',
          title: 'Resource',
          component: DynamicDataFilter({
            searchable: true,
            showAllOptions: false,
            searchPlaceholder: 'Search resource',
          }),
          isActive: !isEmpty(filtersData.resource),
          type: SideFilterType.Checkbox,
          data: toFilterView({
            filterData: filtersData.resource,
            shouldCapitalize: false,
          }),
          collapsible: true,
        },
        index: 2,
      },
      {
        filter: {
          key: 'tag',
          title: 'Compliance framework',
          component: DynamicDataFilter({
            searchable: true,
            showAllOptions: false,
            searchPlaceholder: 'Search framework',
          }),
          isActive: !isEmpty(filtersData.tags),
          type: SideFilterType.Checkbox,
          data: toFilterView({
            filterData: filtersData.tags,
            shouldCapitalize: false,
          }),
          collapsible: true,
        },
        index: 3,
      },
    ]
  )
  const fullDebouncedValues = removeEmptyFilters(currentDebouncedValues)

  const filtersToDisplay = transformFiltersToDisplay(
    omit(fullDebouncedValues, 'assignees'),
    { tags: filtersData.tags }
  )
  const assigneesFilterToDisplay = transformAssigneeFiltersToDisplay(
    pick(fullDebouncedValues, 'assignees'),
    assignableMembers
  )

  const currentFiltersData = LsortBy(
    concat(filtersToDisplay, assigneesFilterToDisplay),
    'filterName'
  )
  const filterActions = useFiltersActions({
    setFilter: onDebouncedValueChange,
    trackFilter,
    clearFilter: clearQueryStringParam,
  })

  return (
    <Flex sx={{ height: '100%', flexDirection: 'column' }}>
      {!isEmpty(currentFiltersData) && (
        <Box sx={{ mb: 3 }}>
          <AppliedFilters
            filters={currentFiltersData}
            onClearAll={() =>
              filterActions.onCheckboxFiltersClear(
                currentFiltersData.map(({ filterKey }) => filterKey)
              )
            }
            onRemoveFilter={filterActions.onCheckboxFilterCheck}
            onClearFilter={filterActions.onCheckboxFiltersClear}
          />
        </Box>
      )}
      <Flex sx={{ height: '0', flex: 1 }}>
        {!issueId && !isHiddenFilters && (
          <FiltersSidePanel>
            <Box sx={{ mb: 2 }} />
            <PathFilter
              path={currentDebouncedValues.path}
              onValueChanged={onDebouncedValueChange}
              isRefreshing={isRefreshing}
            />
            <IacListSideFilters
              filtersValues={currentDebouncedValues}
              filterActions={filterActions}
              isDataRefreshing={isRefreshing}
              filtersMetadata={filtersMetadata}
            />
          </FiltersSidePanel>
        )}
        <Box sx={{ width: '100%', height: '100%' }}>
          {loaded ? (
            <IacFilesList
              isDownloadingExport={isDownloadingExport}
              onPaginationChange={onPaginationChange}
              currentQueryStringParams={currentQueryStringParams}
              clearQueryStringParam={clearQueryStringParam}
              totalSecrets={pagination?.total}
              iacFiles={iacFiles}
              onChangeFileExpansion={onChangeFileExpansion}
              expandedFile={expandedFile}
              isRefreshing={isRefreshing}
              isRefreshingIacFileDetails={isRefreshingIacFileDetails}
              actionHandlers={actionHandlers}
              onIssueSelectionChanged={onIssueSelectionChanged}
              selectedIssues={selectedIssues}
              exportActions={exportActions}
              integrations={integrations}
              onActionIntegrationSubmit={onActionIntegrationSubmit}
              assignableMembers={assignableMembers}
              isLoadingAssignableMembers={isLoadingAssignableMembers}
              isAssignableMembersLoaded={isAssignableMembersLoaded}
              isNotActiveAsset={asset.isNotActive}
              isDataFiltered={isDataFiltered}
              setQueryStringParam={setQueryStringParam}
              trackSort={trackSort}
              sortOptions={sortOptions}
            />
          ) : (
            <Loading />
          )}
        </Box>
      </Flex>
    </Flex>
  )
}

export default IacView
